import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, useTheme, useMediaQuery, Tooltip, styled, FormControl, Select, InputLabel, MenuItem, Autocomplete, } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Transportista, RouteGroup } from '../models'; // Asegúrate de que la ruta sea correcta
import { useState } from 'react'; // Importar useState
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import { Add } from '@mui/icons-material';
import { doc, Timestamp, collection, query, limit, orderBy, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { TextField, Grid, Rating } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { es as esLocale } from 'date-fns/locale';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';

interface TransportistaProps {
    routeGroups: RouteGroup[];
    transportistas: Transportista[];
    handleViewDetails: (row: Transportista) => void;
    handleEditItem: (row: Transportista) => void;
    onAddItem: (row: Transportista) => void;
    handleDeleteItem: (id?: string) => void;
    getData: () => Promise<void>;
    setSelectedSection: (section: string) => void;
}

interface ValidationErrors {
    name?: string;
    phoneNumber?: string;
    placa?: string;
    cedula?: string;
    vencimientoLicencia?: string;
    nota?: string;
    direccion?: string;
    rating?: string;
    email?: string;
}


// Define styled components
const DarkOverlay = styled(Box)({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    zIndex: 9998,
    animation: 'fadeIn 0.3s ease-out',
    '@keyframes fadeIn': {
        '0%': {
            opacity: 0,
        },
        '100%': {
            opacity: 1,
        },
    },
});

const NotificationPopup = styled(Box)({
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    padding: '32px 48px',
    borderRadius: '24px',
    boxShadow: '0 8px 40px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    zIndex: 9999,
    animation: 'slideIn 0.5s ease-out',
    '@keyframes slideIn': {
        '0%': {
            transform: 'translate(-50%, -40%)',
            opacity: 0,
        },
        '100%': {
            transform: 'translate(-50%, -50%)',
            opacity: 1,
        },
    },
});

const AnimatedEmoji = styled('span')({
    display: 'inline-block',
    animation: 'bounce 1s ease infinite',
    fontSize: '2.5rem',
    '@keyframes bounce': {
        '0%, 100%': {
            transform: 'translateY(0)',
        },
        '50%': {
            transform: 'translateY(-8px)',
        },
    },
});

// Add styled components
const ErrorNotificationPopup = styled(Box)(({ theme }) => ({
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    zIndex: 1301,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '2px solid #ff6b6b'
}));


const TransportistaCRUD: React.FC<TransportistaProps> = ({
    routeGroups,
    transportistas,
    handleViewDetails,
    handleEditItem,
    onAddItem,
    handleDeleteItem,
    getData,
    setSelectedSection
}) => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // Estado para manejar la visibilidad del diálogo
    const [transportistaIdToDelete, setTransportistaIdToDelete] = useState<string | null>(null); // Estado para almacenar el ID del transportista a eliminar
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [transportista, setTransportista] = useState<Transportista | null>(null);
    // Add validation state
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [showNotification, setShowNotification] = useState(false);
    const [selectedRouteGroup, setSelectedRouteGroup] = useState<string[]>([]);
    const [selectedRouteGroupName, setSelectedRouteGroupName] = useState<string[]>([]);
    // const [selectedDate, setSelectedDate] = useState<string>('');
    const [showErrorNotification, setShowErrorNotification] = useState(false);

    const validateField = (name: string, value: any): string => {
        switch (name) {
            case 'name':
                return !value ? 'El nombre es requerido' : value.length < 3 ? 'Mínimo 3 caracteres' : '';
            case 'phoneNumber':
                return !value ? 'El teléfono es requerido' : !/^\d{8,}$/.test(value) ? 'Teléfono inválido' : '';
            case 'placa':
                return !value ? 'La placa es requerida' : '';
            case 'cedula':
                return !value ? 'La cédula es requerida' : !/^\d{9,}$/.test(value) ? 'Cédula inválida' : '';
            case 'nota':
                return value?.length > 500 ? 'La nota no puede exceder 500 caracteres' : '';
            case 'direccion':
                return !value ? 'La dirección es requerida' : value.length < 5 ? 'Dirección muy corta' : '';
            case 'rating':
                return !value ? 'El rating es requerido' : value < 0 || value > 10 ? 'Rating debe estar entre 0 y 10' : '';
            case 'email':
                return !value ? 'El correo es requerido' :
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Correo electrónico inválido' : '';
            default:
                return '';
        }
    };

    const handleOpenEditItem = (item: Transportista) => {
        setTransportista({ ...item });
        setOpenEdit(true);
    };

    // Modified handleInputChange
    const handleInputChange = (field: keyof Transportista, value: any) => {
        const error = validateField(field, value);
        setErrors(prev => ({ ...prev, [field]: error }));
        setTransportista(prev => prev ? { ...prev, [field]: value } : null);
    };

    const handleDeleteTransportista = (id: string) => {
        setTransportistaIdToDelete(id); // Establecer el ID del transportista a eliminar
        setDeleteDialogOpen(true); // Abrir el diálogo de confirmación
    };

    const confirmDeleteTransportista = () => {
        if (transportistaIdToDelete) {
            handleDeleteItem(transportistaIdToDelete); // Llamar a la función de eliminación
            setDeleteDialogOpen(false); // Cerrar el diálogo
        }
    };

    const handleOpen = (transportista: Transportista | null = null) => {
        setTransportista(transportista || {
            name: '',
            phoneNumber: '',
            placa: '',
            cedula: '',
            // vencimientoLicencia: Timestamp.fromDate(new Date()),
            vencimientoLicencia: Timestamp.now(),
            nota: '',
            direccion: '',
            rating: 0,
            createdAt: Timestamp.now(),
            listaRutas: [],
            email: ''
        } as Transportista);
        setOpen(true);
    };

    const handleEditItemFunction = async (transportista: Transportista) => {
        setShowNotification(true);
        try {
            await handleEditItem(transportista);
            setOpenEdit(false);
            toast.success('Transportista actualizado exitosamente');
        } catch (error) {
            console.error('Error updating item:', error);
            toast.error('Error al actualizar el transportista');
        } finally {
            await getData();
            setShowNotification(false);
        }
    };

    const getLastTransportistaId = async () => {
        const collectionRef = collection(db, 'transportistas');
        const q = query(collectionRef, orderBy('createdAt', 'desc'), limit(1));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            return querySnapshot.docs[0].id;
        }
        return null;
    };

    const assignTransportistaId = async (transportista: Transportista): Promise<Transportista> => {
        const lastId = await getLastTransportistaId();
        const newId = lastId?.toString() || '';  // Convert to string and provide empty string fallback
        console.log('New ID:', newId);
        return {
            ...transportista,
            id: newId || transportista.id || ''  // Use existing id or empty string as fallback
        };
    };

    const handleAddItem = async () => {
        setShowNotification(true);

        // Create a temporary ID or reference
        if (!transportista) return;

        // Call onAddItem without expecting return value
        await onAddItem(transportista as Transportista);

        const tempTransportista = await assignTransportistaId(transportista);

        // Store current user credentials
        const currentUser = auth.currentUser;

        // Create Firebase Auth user first
        if (transportista?.email) {
            try {
                // Create Firebase Auth user first
                if (transportista?.email) {
                    // Create new user account without signing in
                    await createUserWithEmailAndPassword(
                        auth,
                        transportista.email,
                        "123456"
                    );
                    if (currentUser) {
                        // Esperar a que se complete la actualización del usuario
                        await auth.updateCurrentUser(currentUser);

                        // Ejecutar las siguientes líneas después de la actualización
                        // await getData();
                        // setShowNotification(false);
                        // setSelectedSection('transportistas');
                    }
                }
                setOpen(false);
                setTransportista({
                    name: '',
                    phoneNumber: '',
                    placa: '',
                    cedula: '',
                    vencimientoLicencia: Timestamp.now(),
                    nota: '',
                    direccion: '',
                    rating: 0,
                    createdAt: Timestamp.now(),
                    listaRutas: [],
                    email: ''
                });

            } catch (error: any) {
                if (error.code === 'auth/email-already-in-use') {
                    console.error('tempTransportista.id', tempTransportista.id);
                    handleDeleteItem(tempTransportista.id);
                    setShowErrorNotification(true);
                    setTimeout(() => {
                        setShowErrorNotification(false);
                    }, 3000);
                }
            } finally {
                await getData();
                setShowNotification(false);
                setSelectedSection('transportistas');
            }
        }
        toast.success('Nuevo transportista agregado exitosamente');
    };

    // console.log('Current routeGroups:', routeGroups);
    // console.log('Selected Group:', selectedRouteGroup);
    // console.log('Selected Group Data:', routeGroups?.find(group => group.name === selectedRouteGroup));
    // console.log('Routes Union:', routeGroups?.find(group => group.name === selectedRouteGroup)?.routesUnion);

    return (
        <>
            <Box sx={{ padding: 3 }}>
                <Box sx={{ display: 'contents', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ mb: 3, alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexShrink: 0,
                            color: 'rgb(0, 180, 216)'
                        }}>
                            <h1>Gestión de Transportistas</h1>
                        </Box>
                        <div style={{
                            width: '100%',
                            height: '4px',
                            backgroundColor: 'rgb(0, 180, 216)',
                            margin: '20px 0'
                        }} />
                        <Button
                            startIcon={<Add />}
                            variant="contained"
                            color="primary"
                            onClick={() => handleOpen()}
                            sx={{
                                mb: 6, backgroundColor: '#00b4d8',
                                '&:hover': {
                                    backgroundColor: 'rgb(0, 180, 216)',
                                },
                                fontWeight: 'bold'
                            }}
                        >
                            Agregar Transportista
                        </Button>
                        <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
                            <Table sx={{ minWidth: 650 }} aria-label="transportista table">
                                <TableHead sx={{ backgroundColor: 'rgb(0, 180, 216)' }}>
                                    <TableRow>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nombre</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Teléfono</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Placa</TableCell> {/* Nueva columna para 'placa' */}
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cédula</TableCell> {/* Nueva columna para 'cedula' */}
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Vencimiento Licencia</TableCell> {/* Nueva columna para 'vencimientoLicencia' */}
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Nota</TableCell> {/* Nueva columna para 'nota' */}
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Dirección</TableCell> {/* Nueva columna para 'direccion' */}
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Rating</TableCell> {/* Nueva columna para 'rating' */}
                                        <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {transportistas.map((row: Transportista) => (
                                        <TableRow key={row.id}>
                                            <TableCell component="th" scope="row">{row.name}</TableCell>
                                            <TableCell>{row.phoneNumber}</TableCell>
                                            <TableCell>
                                                <Tooltip title={row.email || ''}>
                                                    <Box sx={{
                                                        maxWidth: '150px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {row.email}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{row.placa}</TableCell> {/* Nueva columna para 'placa' */}
                                            <TableCell>{row.cedula}</TableCell> {/* Nueva columna para 'cedula' */}
                                            <TableCell>
                                                {row.vencimientoLicencia ?
                                                    row.vencimientoLicencia.toDate().toLocaleDateString('es-CR', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit'
                                                    })
                                                    : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={row.nota || ''}>
                                                    <Box sx={{
                                                        maxWidth: '150px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {row.nota}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={row.direccion || ''}>
                                                    <Box sx={{
                                                        maxWidth: '150px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {row.direccion}
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{row.rating}</TableCell> {/* Nueva columna para 'rating' */}
                                            <TableCell align="right">
                                                <Box>
                                                    {/* <IconButton size="small" onClick={() => handleViewDetails(row)} sx={{ color: '#00b4d8' }}>
                                                        <VisibilityIcon />
                                                    </IconButton> */}
                                                    <IconButton size="small" onClick={() => handleOpenEditItem(row)} sx={{ color: '#00b4d8' }}>
                                                        <EditIcon />
                                                    </IconButton>
                                                    {/* <IconButton size="small" onClick={() => row.id && handleDeleteTransportista(row.id)} sx={{ color: 'red' }}>
                                                        <DeleteIcon />
                                                    </IconButton> */}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>
            </Box>

            {/* Diálogo de confirmación de eliminación */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}
                fullScreen={isMobile} // Asegúrate de definir isMobile en tu componente
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                    },
                }}>
                <DialogTitle sx={{
                    backgroundColor: '#87CEEB',
                    color: 'white',
                    fontWeight: 'bold'
                }}>
                    Confirmar Eliminación
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 4, paddingTop: '29px !important' }}>
                    <Box sx={{
                        backgroundColor: 'red',
                        borderRadius: '50%',
                        padding: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginBottom: 2
                    }}>
                        <CloseIcon sx={{ color: 'white', fontSize: 40 }} /> {/* Asegúrate de importar CloseIcon */}
                    </Box>
                    <Typography variant="h6" align="center" sx={{ color: '#333', fontWeight: 'bold' }}>
                        ¿Está seguro de que desea eliminar este transportista?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{
                    justifyContent: 'center',
                    px: 3,
                    py: 2,
                    backgroundColor: 'rgba(135, 206, 235, 0.1)'
                }}>
                    <Button onClick={() => setDeleteDialogOpen(false)}
                        variant="outlined"
                        sx={{
                            color: '#87CEEB',
                            borderColor: '#87CEEB',
                            '&:hover': {
                                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                                borderColor: '#5CACEE',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                            mr: 2,
                        }}>
                        Cancelar
                    </Button>
                    <Button onClick={confirmDeleteTransportista}
                        variant="contained"
                        sx={{
                            backgroundColor: '#87CEEB',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgb(255, 0, 0)',
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(255, 0, 0, 0.5)',
                                color: 'rgba(255, 255, 255, 0.7)',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                        }}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{
                    backgroundColor: '#87CEEB',
                    color: 'white',
                    fontWeight: 'bold'
                }}>Editar Transportista</DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Nombre"
                                    name="name"
                                    value={transportista?.name || ''}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Teléfono"
                                    name="phoneNumber"
                                    value={transportista?.phoneNumber || ''}
                                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                                    error={!!errors.phoneNumber}
                                    helperText={errors.phoneNumber}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        padding: '16px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '4px',
                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                        color: 'rgba(0, 0, 0, 0.87)',
                                        width: '100%'
                                    }}
                                >
                                    {transportista?.email || ''}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Placa"
                                    name="placa"
                                    value={transportista?.placa || ''}
                                    onChange={(e) => handleInputChange('placa', e.target.value)}
                                    error={!!errors.placa}
                                    helperText={errors.placa}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Cédula"
                                    name="cedula"
                                    value={transportista?.cedula || ''}
                                    onChange={(e) => handleInputChange('cedula', e.target.value)}
                                    error={!!errors.cedula}
                                    helperText={errors.cedula}
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Rating"
                                    name="rating"
                                    type="number"
                                    InputProps={{
                                        inputProps: {
                                            min: 0,
                                            max: 10,
                                            step: 1
                                        }
                                    }}
                                    value={transportista?.rating || 0}
                                    onChange={(e) => handleInputChange('rating', Number(e.target.value))}
                                    error={!!errors.rating}
                                    helperText={errors.rating}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Nota"
                                    name="nota"
                                    multiline
                                    rows={4}
                                    value={transportista?.nota || ''}
                                    onChange={(e) => handleInputChange('nota', e.target.value)}
                                    error={!!errors.nota}
                                    helperText={errors.nota}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Vencimiento Licencia"
                                        value={transportista?.vencimientoLicencia?.toDate() || new Date()}
                                        onChange={(newValue) => {
                                            if (newValue) {
                                                setTransportista(prev => prev ? {
                                                    ...prev,
                                                    vencimientoLicencia: Timestamp.fromDate(newValue)
                                                } : null);
                                            }
                                        }}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                variant: "outlined",
                                                error: !!errors.vencimientoLicencia,
                                                helperText: errors.vencimientoLicencia
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Dirección"
                                    name="direccion"
                                    value={transportista?.direccion || ''}
                                    onChange={(e) => handleInputChange('direccion', e.target.value)}
                                    error={!!errors.direccion}
                                    helperText={errors.direccion}
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12} sm={12}>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        multiple
                                        value={transportista?.listaRutas ? routeGroups?.filter(group => transportista.listaRutas.includes(group.id?.toString() || ''))
                                            : (selectedRouteGroupName ? routeGroups?.filter(group => selectedRouteGroupName.includes(group.name)) : [])}
                                        onChange={(event, newValue: RouteGroup[]) => {
                                            setSelectedRouteGroupName(newValue.map(group => group.name));
                                            setSelectedRouteGroup(newValue.map(group => group.id?.toString() || ''));
                                            setTransportista(prev => prev ? {
                                                ...prev,
                                                listaRutas: newValue ? newValue.map(group => group.id?.toString() || '') : []
                                            } : null);
                                        }}
                                        options={routeGroups?.filter(route => {
                                            // Filter out routes that are assigned to other transportistas
                                            const isRouteAssignedToOthers = transportistas?.some(t =>
                                                t.id !== transportista?.id && // Exclude current transportista
                                                t.listaRutas?.includes(route.id?.toString() || '')
                                            );
                                            return !isRouteAssignedToOthers;
                                        }) ?? []}
                                        getOptionLabel={(option: RouteGroup) => option?.name || ''}
                                        isOptionEqualToValue={(option, value) => option.name === value.name}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Rutas"
                                                variant="outlined"
                                            />
                                        )}
                                        noOptionsText="No hay rutas disponibles"
                                    />
                                </FormControl>
                            </Grid>
                            {/* <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Fecha</InputLabel>
                                    <Select
                                        value={selectedDate || ''}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        label="Fecha"
                                        disabled={!selectedRouteGroup}
                                    >
                                        {(() => {
                                            const selectedGroup = routeGroups?.find(group => group.name === selectedRouteGroup);
                                            const dates = selectedGroup?.routesUnion || [];

                                            return dates.length > 0 ? (
                                                dates.map(([_, date, __], index) => (
                                                    <MenuItem key={index} value={date instanceof Date ? date.toISOString() : date}>
                                                        {date instanceof Date ?
                                                            date.toLocaleDateString() :
                                                            new Date(date).toLocaleDateString()
                                                        }
                                                    </MenuItem>
                                                ))
                                            ) : (
                                                <MenuItem value="">No hay fechas disponibles</MenuItem>
                                            );
                                        })()}
                                    </Select>
                                </FormControl>
                            </Grid> */}
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{
                    justifyContent: 'center',
                    px: 3,
                    py: 2,
                    backgroundColor: 'rgba(135, 206, 235, 0.1)'
                }}>
                    <Button
                        onClick={() => setOpenEdit(false)}
                        sx={{
                            color: '#87CEEB',
                            borderColor: '#87CEEB',
                            '&:hover': {
                                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                                borderColor: '#5CACEE',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                            mr: 2,
                        }}
                    >Cancelar</Button>
                    <Button onClick={() => transportista && handleEditItemFunction(transportista)} variant="contained"
                        sx={{
                            backgroundColor: '#87CEEB',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#5CACEE',
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(135, 206, 235, 0.5)',
                                color: 'rgba(255, 255, 255, 0.7)',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                        }}>Actualizar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{
                    backgroundColor: '#87CEEB',
                    color: 'white',
                    fontWeight: 'bold'
                }}>Agregar Transportista</DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Nombre"
                                    name="name"
                                    value={transportista?.name || ''}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Teléfono"
                                    name="phoneNumber"
                                    value={transportista?.phoneNumber || ''}
                                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                                    error={!!errors.phoneNumber}
                                    helperText={errors.phoneNumber}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        padding: '16px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '4px',
                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                        color: 'rgba(0, 0, 0, 0.87)',
                                        width: '100%'
                                    }}
                                >
                                    {transportista?.email || ''}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Placa"
                                    name="placa"
                                    value={transportista?.placa || ''}
                                    onChange={(e) => handleInputChange('placa', e.target.value)}
                                    error={!!errors.placa}
                                    helperText={errors.placa}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Cédula"
                                    name="cedula"
                                    value={transportista?.cedula || ''}
                                    onChange={(e) => handleInputChange('cedula', e.target.value)}
                                    error={!!errors.cedula}
                                    helperText={errors.cedula}
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Rating"
                                    name="rating"
                                    type="number"
                                    InputProps={{
                                        inputProps: {
                                            min: 0,
                                            max: 10,
                                            step: 1
                                        }
                                    }}
                                    value={transportista?.rating || 0}
                                    onChange={(e) => handleInputChange('rating', Number(e.target.value))}
                                    error={!!errors.rating}
                                    helperText={errors.rating}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Nota"
                                    name="nota"
                                    multiline
                                    rows={4}
                                    value={transportista?.nota || ''}
                                    onChange={(e) => handleInputChange('nota', e.target.value)}
                                    error={!!errors.nota}
                                    helperText={errors.nota}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Vencimiento Licencia"
                                        value={transportista?.vencimientoLicencia?.toDate() || new Date()}
                                        onChange={(newValue) => {
                                            if (newValue) {
                                                setTransportista(prev => prev ? {
                                                    ...prev,
                                                    vencimientoLicencia: Timestamp.fromDate(newValue)
                                                } : null);
                                            }
                                        }}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                variant: "outlined",
                                                error: !!errors.vencimientoLicencia,
                                                helperText: errors.vencimientoLicencia
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Dirección"
                                    name="direccion"
                                    value={transportista?.direccion || ''}
                                    onChange={(e) => handleInputChange('direccion', e.target.value)}
                                    error={!!errors.direccion}
                                    helperText={errors.direccion}
                                    variant="outlined"
                                />
                            </Grid>

                            <Grid item xs={12} sm={12}>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        multiple
                                        value={transportista?.listaRutas ? routeGroups?.filter(group => transportista.listaRutas.includes(group.id?.toString() || ''))
                                            : (selectedRouteGroupName ? routeGroups?.filter(group => selectedRouteGroupName.includes(group.name)) : [])}
                                        onChange={(event, newValue: RouteGroup[]) => {
                                            setSelectedRouteGroupName(newValue.map(group => group.name));
                                            setSelectedRouteGroup(newValue.map(group => group.id?.toString() || ''));
                                            setTransportista(prev => prev ? {
                                                ...prev,
                                                listaRutas: newValue ? newValue.map(group => group.id?.toString() || '') : []
                                            } : null);
                                        }}
                                        options={routeGroups?.filter(route => {
                                            // Filter out routes that are assigned to other transportistas
                                            const isRouteAssignedToOthers = transportistas?.some(t =>
                                                t.id !== transportista?.id && // Exclude current transportista
                                                t.listaRutas?.includes(route.id?.toString() || '')
                                            );
                                            return !isRouteAssignedToOthers;
                                        }) ?? []}
                                        getOptionLabel={(option: RouteGroup) => option?.name || ''}
                                        isOptionEqualToValue={(option, value) => option.name === value.name}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Rutas"
                                                variant="outlined"
                                            />
                                        )}
                                        noOptionsText="No hay rutas disponibles"
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{
                    justifyContent: 'center',
                    px: 3,
                    py: 2,
                    backgroundColor: 'rgba(135, 206, 235, 0.1)'
                }}>
                    <Button
                        onClick={() => setOpen(false)}
                        sx={{
                            color: '#87CEEB',
                            borderColor: '#87CEEB',
                            '&:hover': {
                                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                                borderColor: '#5CACEE',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                            mr: 2,
                        }}
                    >Cancelar</Button>
                    <Button onClick={handleAddItem} variant="contained"
                        sx={{
                            backgroundColor: '#87CEEB',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: '#5CACEE',
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(135, 206, 235, 0.5)',
                                color: 'rgba(255, 255, 255, 0.7)',
                            },
                            fontWeight: 'bold',
                            px: 4,
                            py: 1,
                        }}>Agregar</Button>
                </DialogActions>
            </Dialog>

            {showNotification && (
                <>
                    <DarkOverlay />
                    <NotificationPopup>
                        <AnimatedEmoji style={{ animationDelay: '0s' }}>✨</AnimatedEmoji>
                        <Typography
                            sx={{
                                color: '#1a1a1a',
                                fontWeight: 600,
                                fontSize: '1.5rem',
                                letterSpacing: '-0.02em',
                            }}
                        >
                            Actualizando
                        </Typography>
                        <AnimatedEmoji style={{ animationDelay: '0.2s' }}>🎉</AnimatedEmoji>
                    </NotificationPopup>
                </>
            )}

            {showErrorNotification && (
                <>
                    <ErrorNotificationPopup>
                        <AnimatedEmoji style={{ animationDelay: '0s' }}>⚠️</AnimatedEmoji>
                        <Typography
                            sx={{
                                color: '#1a1a1a',
                                fontWeight: 600,
                                fontSize: '1.5rem',
                                letterSpacing: '-0.02em',
                                textAlign: 'center'
                            }}
                        >
                            Error al guardar transportista
                        </Typography>
                        <Typography
                            sx={{
                                color: '#666',
                                textAlign: 'center'
                            }}
                        >
                            Ya existe un transportista registrado con esta Email.
                            Por favor verifique el Email e intente nuevamente.
                        </Typography>
                        <AnimatedEmoji style={{ animationDelay: '0.2s' }}>❌</AnimatedEmoji>
                    </ErrorNotificationPopup>
                </>
            )}

        </>
    );
};

export default TransportistaCRUD;